import {
  CATEGORIAS,
  SUBCATEGORIAS,
  duracaoMinutos,
  sessaoEsquecida,
  type Categoria,
  type Sessao,
} from '@sheafyield/dominio';
import { apagar, gravar, type Banco } from '@sheafyield/sync';
import { useEffect, useState } from 'react';

import {
  banco as bancoDoApp,
  cliente as clienteDoApp,
  motor as motorDoApp,
} from '../app/contexto.js';
import { sessaoAberta } from '../dados/consultas.js';
import { donoDaSessao, type DonoDasLinhas } from '../dados/dono.js';
import { useConsultaCom, type MotorObservavel } from '../dados/useConsulta.js';
import { Botao } from '../ds/Botao.js';
import { Selecao } from '../ds/Selecao.js';
import { Icone } from '../ds/icones.js';
import { GrupoDeChips } from '../ds/GrupoDeChips.js';
import { duracaoPorExtenso, useViradaDeMinuto } from './relogio.js';
import './cronometro.css';

type Subcategoria = NonNullable<Sessao['subcategoria']>;

const ROTULO_DA_CATEGORIA: Record<Categoria, string> = {
  campo: 'Campo',
  carrinho: 'Carrinho',
  telefone: 'Telefone',
  cartas: 'Cartas',
  credito: 'Crédito',
};
const ROTULO_DA_SUBCATEGORIA: Record<Subcategoria, string> = {
  ldc: 'LDC',
  betel: 'Betel',
  escola: 'Escola',
  socorro: 'Socorro',
};
const OPCOES_DE_CATEGORIA = CATEGORIAS.map((valor) => ({
  valor,
  rotulo: ROTULO_DA_CATEGORIA[valor],
}));
const OPCOES_DE_SUBCATEGORIA = SUBCATEGORIAS.map((valor) => ({
  valor,
  rotulo: ROTULO_DA_SUBCATEGORIA[valor],
}));
/** O tipo de crédito que já vem marcado quando a revelação abre. Escolher
 *  "Crédito" e não escolher o tipo gravaria `subcategoria: null`, e o
 *  relatório de créditos não saberia de qual crédito se trata; LDC é o mais
 *  comum, e trocar continua sendo um toque. */
const SUBCATEGORIA_PADRAO: Subcategoria = 'ldc';

/** Quantos segundos o "Desfazer" do cronômetro fica na tela — os mesmos seis
 *  da folha Registrar, porque é a mesma promessa. */
const SEGUNDOS_DE_DESFAZER = 6;

/** O dono do app, amarrado uma vez: o cliente é singleton, e recriar a
 *  função a cada render não traria nada. */
const donoDoApp = donoDaSessao(clienteDoApp);

/** O par que descreve o que se está fazendo. `subcategoria` só existe em
 *  `credito`; nas outras categorias é nula por construção. */
interface Escolha {
  categoria: Categoria;
  subcategoria: Subcategoria | null;
}

function normalizar(
  categoria: Categoria,
  subcategoria: Subcategoria | null,
): Escolha {
  return {
    categoria,
    subcategoria: categoria === 'credito' ? subcategoria : null,
  };
}

/**
 * O que desfazer o último toque do cronômetro exige: a sessão que ele criou
 * (para sepultar) e o retrato da que ele mexeu (para pôr de volta).
 *
 * As quatro ações cabem neste par. Iniciar cria e não mexe em nada;
 * pausar mexe e não cria; reetiquetar mexe e não cria; trocar de categoria
 * com tempo na conta cria **e** mexe. Desfazer é sempre a ordem inversa da
 * gravação.
 */
interface Reversao {
  /** O que dizer no aviso: "Cronômetro iniciado", "Pausado"… */
  rotulo: string;
  criada: string | null;
  anterior: Sessao | null;
}

function ehSubcategoria(valor: string): valor is Subcategoria {
  return (SUBCATEGORIAS as readonly string[]).includes(valor);
}
function ehCategoria(valor: string): valor is Categoria {
  return (CATEGORIAS as readonly string[]).includes(valor);
}

export interface DepsDoCronometro {
  banco: Banco;
  motor: MotorObservavel;
  /** De quem são as linhas que este cronômetro grava. Injetado, e não
   *  descoberto do banco: o dono é da sessão de autenticação (ver
   *  `dados/dono.ts`), e o componente não tem por que saber de onde. */
  dono: DonoDasLinhas;
}

/**
 * O cronômetro de horas de campo.
 *
 * O tempo na tela é **derivado** de `inicio` e do relógio, nunca de um
 * contador em memória: a PWA do iPhone é descarregada a qualquer momento, e
 * um contador em RAM perderia a hora inteira. Reabrir o app com uma sessão
 * aberta mostra o tempo certo porque a única coisa guardada é o instante em
 * que começou.
 *
 * Um toque inicia, um toque pausa. Pausar **fecha** a sessão corrente e
 * retomar abre outra (decisão da etapa: o schema tem `inicio` e `fim`, não
 * uma lista de pausas — somar sessões dá o total certo e sincroniza sem
 * inventar coluna).
 *
 * **Trocar de categoria com sessão em andamento é pausar e retomar no mesmo
 * instante**, não um erro a bloquear. No campo se percebe no meio da manhã
 * que o carrinho começou; exigir pausar-trocar-retomar seriam dois toques a
 * mais no exato momento em que a pessoa está ocupada, e a alternativa
 * (chips travados) esconderia tempo na categoria errada, que é o que o
 * relatório do mês não perdoa. A sessão nova começa exatamente no `fim` da
 * anterior: nenhum minuto se perde na troca.
 *
 * Esta é a forma injetável, para teste. A tela usa `Cronometro`.
 */
export function CronometroCom({ banco, motor, dono }: DepsDoCronometro) {
  // `?? null` para separar as duas ausências: `undefined` é "a leitura ainda
  // não voltou" e `null` é "não há sessão aberta". Sem isso o botão diria
  // "Iniciar" antes de o banco responder.
  const { dados: emCurso, erro: erroDeLeitura } = useConsultaCom(
    motor,
    'sessao-aberta',
    async () => (await sessaoAberta(banco)) ?? null,
  );
  const lendo = emCurso === undefined;
  const rodando = emCurso !== undefined && emCurso !== null;

  // A última escolha, para quando **não** há sessão aberta. Com sessão
  // aberta quem manda são os campos dela: o banco é a fonte da verdade, e
  // dois lugares guardando a mesma coisa é como se desencontram.
  const [escolha, setEscolha] = useState<Escolha>({
    categoria: 'campo',
    subcategoria: null,
  });
  const emUso: Escolha = rodando
    ? { categoria: emCurso.categoria, subcategoria: emCurso.subcategoria }
    : escolha;

  const [ocupado, setOcupado] = useState(false);
  const [erroDeEscrita, setErroDeEscrita] = useState<Error | null>(null);
  /** O último toque, enquanto ele ainda pode ser desfeito. */
  const [reversao, setReversao] = useState<Reversao | null>(null);

  // O tique é `useViradaDeMinuto` (ver `relogio.ts`): um contador alinhado à
  // virada do minuto da própria sessão. Aqui ele marca **ao montar**, porque
  // o mostrador é o relógio — o que ele mostra vem de `inicio` mais o
  // instante de agora, e montar sem ler o relógio deixaria o número parado
  // no valor do primeiro render por até um minuto ao reabrir o app.
  const inicioMs = rodando ? Date.parse(emCurso.inicio) : null;
  const virada = useViradaDeMinuto(inicioMs, 'ao montar');
  // O relógio é lido **quando o contador anda**, e não guardado em estado
  // por um efeito: um render qualquer no meio do minuto (o `ocupado` do
  // botão, por exemplo) reaproveita a mesma leitura, e o mostrador não muda
  // fora da virada.
  //
  // Isto é estado ajustado no render, e não um `useMemo`, por duas razões. A
  // primeira é que o `useMemo` **não é garantia de cache**: o React pode
  // descartar o valor e recalcular quando quiser, e aí o mostrador mudaria
  // no meio do minuto — exatamente o que este trecho existe para impedir. A
  // segunda é que `useMemo(() => new Date(), [virada])` era o **único aviso
  // de lint do repositório** (`react-hooks/exhaustive-deps`: `virada` não
  // aparece no cálculo), e aviso tolerado é aviso que esconde o próximo.
  //
  // O padrão é o que o React documenta para "ajustar estado quando uma
  // entrada muda": comparar, trocar, e usar **o valor local** neste render —
  // o `leitura` do closure ainda é o velho até o React reexecutar. Uma ref
  // faria o mesmo, mas ler `.current` no render é o que `react-hooks/refs`
  // proíbe, e com razão: ref não avisa o React de que algo mudou.
  const [leitura, setLeitura] = useState(() => ({ virada, agora: new Date() }));
  let atual = leitura;
  if (atual.virada !== virada) {
    atual = { virada, agora: new Date() };
    setLeitura(atual);
  }
  const agora = atual.agora;

  // O mostrador usa a mesma `duracaoMinutos` do relatório, e por isso herda o
  // mesmo teto: uma sessão aberta conta até a meia-noite do dia em que
  // começou. Sem ele o mostrador dizia 30 h enquanto o cartão "Hoje" dizia
  // zero (o recorte do dia é pelo `inicio`, e aquele dia já passou) e o "No
  // mês" dizia centenas de horas — três números da mesma tela em desacordo.
  const minutos = rodando ? duracaoMinutos(emCurso, agora) : 0;
  const esquecida = rodando && sessaoEsquecida(emCurso, agora);

  /** O dono chega resolvido: `dono()` vai à sessão de autenticação, e
   *  esperar por algo que não é do Dexie dentro de uma transação a mataria
   *  ("Transaction committed too early"). */
  async function abrirSessao(
    escolhida: Escolha,
    quando: Date,
    donoAtual: string,
  ): Promise<Sessao> {
    return gravar(banco, 'sessao', {
      id: crypto.randomUUID(),
      user_id: donoAtual,
      inicio: quando.toISOString(),
      fim: null,
      categoria: escolhida.categoria,
      subcategoria: escolhida.subcategoria,
      nota: '',
    });
  }

  /** Fecha a sessão — ou a apaga, quando não chegou a um minuto. "Toquei sem
   *  querer e toquei de novo" não pode deixar linha nenhuma para o relatório
   *  do mês somar, e o `apagar` é lápide de propósito: a linha pode já ter
   *  subido, e um `delete` local a traria de volta no próximo pull. */
  async function fecharSessao(sessao: Sessao, quando: Date): Promise<void> {
    if (duracaoMinutos(sessao, quando) === 0) {
      await apagar(banco, 'sessao', sessao.id);
      return;
    }
    const { updated_at, server_updated_at, ...resto } = sessao;
    void updated_at;
    void server_updated_at;
    await gravar(banco, 'sessao', { ...resto, fim: quando.toISOString() });
  }

  /** Só troca os rótulos da sessão aberta, sem fechar nada: é o caminho da
   *  troca de categoria dentro do primeiro minuto, em que fechar deixaria
   *  uma linha de duração zero. */
  async function reetiquetar(sessao: Sessao, nova: Escolha): Promise<void> {
    const { updated_at, server_updated_at, ...resto } = sessao;
    void updated_at;
    void server_updated_at;
    await gravar(banco, 'sessao', {
      ...resto,
      categoria: nova.categoria,
      subcategoria: nova.subcategoria,
    });
  }

  /** Põe de volta o que o último toque mexeu: sepulta a sessão que ele criou
   *  e restaura o retrato da que ele fechou ou reetiquetou. A ordem é a
   *  inversa da gravação, e as duas metades são idempotentes — tentar de novo
   *  depois de uma falha refaz o par inteiro.
   *
   *  Lápide, nunca remoção: a linha pode já ter subido, e um `delete` local a
   *  traria de volta no próximo pull. Os carimbos ficam de fora na
   *  restauração — `gravar` põe os seus, e um `updated_at` velho seria
   *  recusado pelo LWW. */
  function desfazer(): void {
    const r = reversao;
    if (r === null) return;
    comGuarda(async () => {
      if (r.criada !== null) await apagar(banco, 'sessao', r.criada);
      if (r.anterior !== null) {
        const { updated_at, server_updated_at, ...comoEra } = r.anterior;
        void updated_at;
        void server_updated_at;
        await gravar(banco, 'sessao', comoEra);
        // A escolha volta com ela: o que a tela mostra sem sessão aberta é o
        // último par escolhido, e depois de desfazer uma pausa esse par é o
        // da sessão restaurada.
        setEscolha({
          categoria: r.anterior.categoria,
          subcategoria: r.anterior.subcategoria,
        });
      }
      setReversao(null);
    });
  }

  // O Desfazer do cronômetro dura os mesmos seis segundos do da folha
  // Registrar, e some sozinho: uma sessão iniciada há meia hora não pode ter
  // um botão ao lado que a apaga inteira. O relógio reinicia a cada toque
  // novo porque `reversao` troca de identidade.
  useEffect(() => {
    if (reversao === null) return;
    const relogio = setTimeout(() => {
      setReversao(null);
    }, SEGUNDOS_DE_DESFAZER * 1000);
    return () => clearTimeout(relogio);
  }, [reversao]);

  function comGuarda(acao: () => Promise<void>): void {
    if (ocupado || lendo) return;
    setOcupado(true);
    setErroDeEscrita(null);
    void acao()
      .catch((causa: unknown) => {
        setErroDeEscrita(
          causa instanceof Error ? causa : new Error(String(causa)),
        );
      })
      .finally(() => {
        setOcupado(false);
      });
  }

  function aoTocarNoBotao(): void {
    comGuarda(async () => {
      if (emCurso === undefined) return;
      if (emCurso === null) {
        const donoAtual = await dono();
        const nova = await abrirSessao(emUso, new Date(), donoAtual);
        setReversao({
          rotulo: 'Cronômetro iniciado',
          criada: nova.id,
          anterior: null,
        });
        return;
      }
      const quando = new Date();
      // Pausar deixa os chips como estavam: quem parou às 11h no carrinho
      // retoma no carrinho, sem reescolher.
      setEscolha({
        categoria: emCurso.categoria,
        subcategoria: emCurso.subcategoria,
      });
      await fecharSessao(emCurso, quando);
      // O retrato é o de **antes** de fechar, com `fim: null`: restaurá-lo é
      // reabrir a sessão exatamente onde ela estava — inclusive quando o
      // fechamento foi uma lápide (a pausa antes de um minuto).
      setReversao({
        rotulo: 'Cronômetro pausado',
        criada: null,
        anterior: emCurso,
      });
    });
  }

  function escolher(nova: Escolha): void {
    comGuarda(async () => {
      setEscolha(nova);
      if (emCurso === undefined || emCurso === null) return;
      // O dono sai da sessão de autenticação antes de a transação abrir:
      // esperar por algo que não é do Dexie lá dentro mata a transação.
      const donoAtual = await dono();
      const quando = new Date();
      // Dentro do primeiro minuto não há tempo a preservar: reetiquetar em
      // vez de fechar evita justamente a linha de duração zero.
      if (duracaoMinutos(emCurso, quando) === 0) {
        await reetiquetar(emCurso, nova);
        setReversao({
          rotulo: 'Categoria trocada',
          criada: null,
          anterior: emCurso,
        });
        return;
      }
      // Uma transação só, e a sessão nova entra **antes** de a anterior
      // fechar. Duas razões:
      //
      // 1. Atomicidade. Em duas transações, uma falha entre elas deixava o
      //    cronômetro sem sessão nenhuma — hora de campo perdida em
      //    silêncio, no meio da manhã.
      // 2. A ordem, por precaução. Hoje o quadro do meio não existe: o sinal
      //    de mutação é adiado para fora da zona de transação e o IndexedDB
      //    serializa transações de escopo sobreposto, então ninguém lê o
      //    estado intermediário. Abrir antes de fechar mantém a ordem certa
      //    mesmo assim — em todo estado intermediário existe sessão aberta,
      //    a nova, que é a mais recente por `inicio` e a que `sessaoAberta`
      //    devolve.
      //
      // O instante é um só: a sessão nova começa exatamente onde a anterior
      // termina, e a soma do dia não perde nem ganha um minuto na troca.
      const aberta = await banco.transaction(
        'rw',
        banco.sessao,
        banco.fila,
        async () => {
          const criada = await abrirSessao(nova, quando, donoAtual);
          await fecharSessao(emCurso, quando);
          return criada;
        },
      );
      setReversao({
        rotulo: 'Categoria trocada',
        criada: aberta.id,
        anterior: emCurso,
      });
    });
  }

  const ehCredito = emUso.categoria === 'credito';
  const erro = erroDeEscrita ?? erroDeLeitura;

  return (
    <section className="cronometro" aria-label="Cronômetro">
      <div className="cronometro__cartao">
        {/* O estado escrito e a saída de Desfazer dividem a mesma região viva.
          A região é uma só, e montada sempre, porque é assim que o leitor de
          tela anuncia: um `role="status"` que **entra** no DOM já com texto é
          mudo, e quem anuncia é a mudança de conteúdo de uma região que já
          estava lá. Foi por isso que o "Desfazer" do cronômetro não virou um
          `Toast`: o aviso de Registrar e este dividiriam o mesmo canto da
          mesma tela, dois `position: fixed` no mesmo lugar, e um cobriria o
          outro no minuto em que se registra uma visita e se pausa o
          cronômetro. Aqui ele nasce ao lado do controle que o produziu. */}
        <div className="cronometro__linha-estado" role="status">
          <p className="cronometro__estado">
            {lendo
              ? 'Carregando…'
              : reversao !== null
                ? reversao.rotulo
                : esquecida
                  ? // Ficou aberta de um dia para o outro: o mostrador parou na
                    // meia-noite, e sem dizer isso ele pareceria quebrado.
                    'Aberta desde ontem'
                  : rodando
                    ? 'Em andamento'
                    : 'Pronto para começar'}
          </p>
          {reversao !== null ? (
            <Botao
              variante="texto"
              className="cronometro__desfazer"
              onClick={desfazer}
            >
              Desfazer
            </Botao>
          ) : null}
        </div>
        <p
          className="cronometro__tempo"
          role="timer"
          aria-label={duracaoPorExtenso(minutos)}
        >
          {Math.floor(minutos / 60)}
          <span>h</span>
          {String(minutos % 60).padStart(2, '0')}
        </p>
        <Botao
          largura="cheia"
          variante="secundario"
          carregando={lendo || ocupado}
          onClick={aoTocarNoBotao}
        >
          <Icone>
            {rodando ? (
              <path d="M6 4h4v16H6zM14 4h4v16h-4z" />
            ) : (
              <path d="m7 3 15 9-15 9Z" />
            )}
          </Icone>
          <span>{rodando ? 'Pausar' : 'Iniciar'}</span>
        </Botao>
      </div>
      <Selecao
        className="cronometro__seletor"
        rotulo="Categoria"
        valor={emUso.categoria}
        opcoes={OPCOES_DE_CATEGORIA}
        aoMudar={(valor) => {
          if (ehCategoria(valor))
            escolher(
              normalizar(valor, emUso.subcategoria ?? SUBCATEGORIA_PADRAO),
            );
        }}
      />
      <GrupoDeChips
        className="cronometro__categorias"
        nome="categoria"
        rotulo="Categoria"
        opcoes={OPCOES_DE_CATEGORIA}
        valor={emUso.categoria}
        aoMudar={(valor) => {
          if (!ehCategoria(valor)) return;
          // Crédito sem tipo gravaria `subcategoria: null`: o padrão entra
          // junto com a revelação, e trocar continua sendo um toque.
          escolher(
            normalizar(valor, emUso.subcategoria ?? SUBCATEGORIA_PADRAO),
          );
        }}
      />
      {/* Safari não interpola `block-size: auto`: a revelação vai por
          `grid-template-rows`, de `0fr` a `1fr`. Fechado, o conteúdo fica
          `inert` — clipado por `overflow` ele continuaria focável e o
          VoiceOver o leria fora da tela. */}
      <div
        className="cronometro__revelacao"
        data-aberta={ehCredito ? 'sim' : 'nao'}
      >
        <div className="cronometro__revelacao-interna" inert={!ehCredito}>
          <GrupoDeChips
            nome="subcategoria"
            rotulo="Tipo de crédito"
            opcoes={OPCOES_DE_SUBCATEGORIA}
            valor={emUso.subcategoria}
            aoMudar={(valor) => {
              if (!ehSubcategoria(valor)) return;
              escolher(normalizar('credito', valor));
            }}
          />
        </div>
      </div>
      {erro ? (
        <p className="cronometro__erro" role="alert">
          Não deu para gravar: {erro.message}
        </p>
      ) : null}
    </section>
  );
}

/** `CronometroCom` amarrado ao banco, ao motor e à sessão do app. É o que a
 *  tela usa. */
export function Cronometro() {
  return (
    <CronometroCom banco={bancoDoApp} motor={motorDoApp} dono={donoDoApp} />
  );
}
